import Backbone from 'backbone'
import http from 'http' // This is the stream-http module shipped w/ browserify
import { Readable } from 'stream'
import split from 'binary-split' // Splitting the chunks in the buffer is faster than string split
import url from 'url'
import $ from 'jquery'
import scoreboardUrl from 'scoreboard/components/scoreboard-url'
import HttpStatusCodes from 'http-status-codes'
import { parseHeaders } from 'scoreboard/setup-xhr-stream'
import errors from 'scoreboard/components/setup-errors'
import Xhr, {isOffline} from 'scoreboard/components/xhr'
import { XHR_HEADERS_AVAILABLE } from 'scoreboard/components/permissions'

const modes = {
	NDJSON: 1,
	BUFFER: 2
}

/*
 * Readable stream that emits tree nodes.
 */
class TreeStream extends Readable {

	constructor (opts) {
		super({objectMode: true}) // Object mode since we're manipulating objects

		if (!opts.url) {
			throw new Error('TreeStream must have defined url')
		}

		// Can't wipe out the error handler or error dialogs will start showing up (#22687)
		// error:true skips the global error handler, but DOES apply the expiredLoginHandler
		// (This is setup globally in setup-jquery.js)
		this._error = opts.error || (xhr => errors.errorHandler(xhr, {error:true}))

		this.mode = modes.NDJSON // Emits nodes as they arrive is the default mode

		this._url = opts.url // Store the relative URL in case we're doing xhr via jquery

		this.requestOptions = url.parse(scoreboardUrl(opts.url), true) // Generates a new url object with the query string properly adjusted
		this.requestOptions.method = opts.method || 'get'

		this._reading = false
		this.finished = false
	}

	/*
	 * Uses the jquery `getJSON` to fetch the JSON from the server and process the JSON using the browser's
	 * native parsing.
	 */
	_bufferedRead () {
		if (this._aborted) return
		this.requestOptions.headers['Accept'] = 'application/json'

		//use regular jQuery ajax call so we can optionally define an error handler so custom behavior can apply
		//when the requests fails instead of always showing the global error handler
		this.xhr = $.get({
			dataType: 'json',
			url: this._url,
			success:  (nodes, textStatus, xhr) => {
				this.emit('loading')
				this.statusCode = this.status = xhr.status
				this.headers = xhr.getAllResponseHeaders()
				this.emit('headers', parseHeaders(this.headers))
				Backbone.trigger(XHR_HEADERS_AVAILABLE, this /* xhr */)

				if (xhr.status === HttpStatusCodes.OK) {
					this.emit('success')
					nodes.forEach(node => this.push(node))
				} else {
					this.emit('error', this)
				}
				this.finished = true
				this.push(null) // emits `end` on the stream
			},
			error: this._error
		})
	}

	/*
	 * Asks the server for each node on a newline using the NDJSON format. Emits the nodes as we receive them.
	 */
	_streamRead () {
		if (this._aborted) return
		this.requestOptions.headers['Accept'] = 'application/x-ndjson, application/json'
		Xhr.setCsrfTokenInHeaderArray(this.requestOptions.headers)

		// execute the http request
		this.req = http.request(this.requestOptions, res => {
			// `res` is the response stream from the http request. See https://github.com/jhiesey/stream-http for details

			this.headers = res.headers // Store the response headers from the server on the tree stream
			this.statusCode = this.status = res.statusCode // Set on the tree stream so interested parties can investigate the status from the server
			this.response = res // to ease debugging

			// #30354 Adding this property so this works with Xhr.isOffline
			res.status = res.statusCode
			if (isOffline(res)) {
				// A 503 is not caught by the error handler, but rather goes
				// through the normal callback, so we have to account for it here
				errors.errorHandler(null)
				return
			}

			this.emit('loading')
			this.emit('headers', this.headers)

			// #21996 trigger this event if the XHR request is permissible to the user.
			// permissions.js listens to this event, it must be before any
			// other event (e.g. 'success') so that permissions checks are made
			// against the newest headers (#36079)
			Backbone.trigger(XHR_HEADERS_AVAILABLE, this /* xhr */)

			if (this.statusCode === HttpStatusCodes.OK) {

				this.emit('success')
				res.pipe(split()) // Split the buffer on new lines. It's faster to split chunks before turning them into strings in most browsers
					.on('data', node => this.push(JSON.parse(node))) // Turn the buffer into a JSON object and emit it on this stream
					.on('end', () => {
						this.finished = true
						this.push(null) // Indicates this stream is empty and finished.
					})
			} else {
				this.data = ''
				res.on('data', data => this.data += data)
					.on('end', () => {
						this.emit('error', this)
						this.finished = true
						this.push(null) // Indicates this stream is empty and finished.
					})
			}
		}).on('error', error => {
			// #30354 Unlike other network request methods, this one only provides
			// an Error object to the handler, rather than a response object. Since
			// this error handler was added for #30354, for now we are just
			// unconditionally treating errors as being offline/unable to contact
			// the server. We can handle other types of errors as they arise
			this.emit('error', this)
			$(document).trigger($.Event('ajaxError'))
		}).end()
	}

	_read () {
		if (this._reading || this.finished) {
			// Prevent issuing further http requests.
			return
		}

		this.requestOptions.headers = {}

		this._reading = true

		if (this.mode === modes.BUFFER) {
			return this._bufferedRead()
		} else if (this.mode === modes.NDJSON) {
			return this._streamRead()
		}
	}

	get responseJSON() {
		// #31389 if we are offline, there may not be any data
		if (!this._responseJSON && this.data) {
			this._responseJSON = JSON.parse(this.data)
		}
		return this._responseJSON
	}

	getResponseHeader(header) {
		return this.headers[header.toLowerCase()]
	}

	abort() {
		let req = this.xhr || this.req
		if (req) {
			req.abort()
		}
		this._aborted = true
	}

}

TreeStream.modes = modes

export default TreeStream

TreeStream.__filename__ = 'components/tree-stream.js'
